// src/features/game/components/PrizesModal.tsx

import React, {
  useState,
} from 'react';

import {
  Plus,
  Trash2,
  X,
} from 'lucide-react';

import type {
  Prize,
} from '../types/game.types';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

interface PrizesModalProps {
  isOpen: boolean;

  onClose: () => void;

  onSubmit: (
    prizes: Prize[],
  ) => Promise<void>;
}

const INITIAL_PRIZES: Prize[] = [
  {
    rank: 1,
    amount: 0,
  },
  {
    rank: 2,
    amount: 0,
  },
  {
    rank: 3,
    amount: 0,
  },
];

const PrizesModal:
  React.FC<PrizesModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
  }) => {
  const modal =
    useModal();

  const [
    prizes,
    setPrizes,
  ] =
    useState<Prize[]>(
      INITIAL_PRIZES,
    );

  const [
    loading,
    setLoading,
  ] =
    useState<boolean>(
      false,
    );

  /**
   * 프라이즈 추가
   */
  const handleAddPrize =
    (): void => {
      const newPrize:
        Prize = {
        rank:
          prizes.length +
          1,

        amount: 0,
      };

      setPrizes(
        (
          previous,
        ) => [
          ...previous,
          newPrize,
        ],
      );
    };

  /**
   * 프라이즈 삭제
   */
  const handleRemovePrize =
    async (
      index: number,
    ): Promise<void> => {
      if (
        prizes.length <=
        1
      ) {
        await modal.alert(
          '최소 1개의 프라이즈가 필요합니다.',
        );

        return;
      }

      setPrizes(
        (
          previous,
        ) =>
          previous
            .filter(
              (
                _,
                prizeIndex,
              ) =>
                prizeIndex !==
                index,
            )
            .map(
              (
                prize,
                prizeIndex,
              ) => ({
                ...prize,

                rank:
                  prizeIndex +
                  1,
              }),
            ),
      );
    };

  /**
   * 금액 변경
   */
  const handleAmountChange = (
    index: number,
    value: string,
  ): void => {
    const amount =
      Number.parseInt(
        value,
        10,
      ) || 0;

    setPrizes(
      (
        previous,
      ) =>
        previous.map(
          (
            prize,
            prizeIndex,
          ) =>
            prizeIndex ===
            index
              ? {
                  ...prize,
                  amount,
                }
              : prize,
        ),
    );
  };

  /**
   * 내부 상태 초기화
   */
  const resetModal =
    (): void => {
      setPrizes(
        INITIAL_PRIZES,
      );
    };

  /**
   * 닫기
   */
  const handleClose =
    (): void => {
      if (loading) {
        return;
      }

      resetModal();
      onClose();
    };

  /**
   * 저장
   */
  const handleSubmit =
    async (): Promise<void> => {
      const hasInvalidPrize =
        prizes.some(
          (
            prize: Prize,
          ) =>
            prize.amount <=
            0,
        );

      if (
        hasInvalidPrize
      ) {
        await modal.alert(
          '모든 프라이즈 금액은 0보다 커야 합니다.',
        );

        return;
      }

      try {
        setLoading(
          true,
        );

        await onSubmit(
          prizes,
        );

        await modal.alert(
          '프라이즈가 설정되었습니다.',
        );

        resetModal();

        onClose();
      } catch (
        error: unknown
      ) {
        console.error(
          '프라이즈 설정 실패:',
          error,
        );

        await modal.alert(
          '프라이즈 설정에 실패했습니다.',
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  /**
   * 총 상금
   */
  const totalPrize =
    prizes.reduce(
      (
        sum: number,
        prize: Prize,
      ) =>
        sum +
        prize.amount,

      0,
    );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="프라이즈 설정"
      onMouseDown={(
        event,
      ) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          handleClose();
        }
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-gray-200 p-5 sm:p-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
              프라이즈 설정
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              순위별 프라이즈를
              설정합니다.
            </p>
          </div>

          <button
            type="button"
            onClick={
              handleClose
            }
            disabled={
              loading
            }
            className="grid h-9 w-9 place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="프라이즈 설정 닫기"
          >
            <X
              size={22}
            />
          </button>
        </header>

        {/* Body */}
        <section className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="space-y-3">
            {prizes.map(
              (
                prize: Prize,
                index: number,
              ) => (
                <div
                  key={
                    prize.rank
                  }
                  className="flex items-center gap-3"
                >
                  <div className="w-16 shrink-0 text-base font-medium text-gray-700 sm:w-20 sm:text-lg">
                    {
                      prize.rank
                    }
                    등
                  </div>

                  <input
                    type="number"
                    value={
                      prize.amount ||
                      ''
                    }
                    onChange={(
                      event,
                    ) =>
                      handleAmountChange(
                        index,
                        event
                          .target
                          .value,
                      )
                    }
                    min={0}
                    step={1000}
                    disabled={
                      loading
                    }
                    placeholder="금액"
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm outline-none transition focus:border-main focus:ring-2 focus:ring-main/10 disabled:bg-gray-100 sm:text-base"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      void handleRemovePrize(
                        index,
                      )
                    }
                    disabled={
                      loading ||
                      prizes.length <=
                        1
                    }
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label={`${prize.rank}등 프라이즈 삭제`}
                  >
                    <Trash2
                      size={20}
                    />
                  </button>
                </div>
              ),
            )}
          </div>

          {/* Add */}
          <button
            type="button"
            onClick={
              handleAddPrize
            }
            disabled={
              loading
            }
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-600 transition hover:border-main hover:text-main disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
          >
            <Plus
              size={20}
            />

            프라이즈 추가
          </button>

          {/* Total */}
          <div className="mt-6 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between gap-4">
              <span className="text-base font-medium text-gray-700 sm:text-lg">
                총 상금 (GTD)
              </span>

              <span className="text-xl font-semibold text-main">
                {totalPrize.toLocaleString(
                  'ko-KR',
                )}
                LP
              </span>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="grid grid-cols-2 gap-3 border-t border-gray-200 p-5 sm:flex sm:justify-end sm:p-6">
          <button
            type="button"
            onClick={
              handleClose
            }
            disabled={
              loading
            }
            className="rounded-lg bg-gray-200 px-6 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
          >
            취소
          </button>

          <button
            type="button"
            onClick={() =>
              void handleSubmit()
            }
            disabled={
              loading
            }
            className="rounded-lg bg-main px-6 py-3 text-sm font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50 sm:text-base"
          >
            {loading
              ? '처리 중...'
              : '설정'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default PrizesModal;